import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Amount, Button, Chip, Field, Input, Panel, PanelHeader, Select } from '@promanage/ui';
import {
  PROPERTY_TYPES,
  formatFeeRate,
  formatRef,
  formatShare,
  managementFee,
  managementFeeIssues,
  streetLine,
} from '@promanage/core';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { useOrgSettings } from '../features/settings/org';
import {
  emptyShare,
  parseSharePercent,
  propertyTypeLabel,
  shareIssues,
  shareTotals,
  usableShares,
  type ShareLine,
} from '../features/property/shares';
import { propertyDetailIssues, wholeNumber } from '../features/property/details';
import { AddressSearch } from '../features/property/AddressSearch';
import { todayIso } from '../features/property/today';
import { PropertyManagerPicker } from '../features/property/PropertyManagers';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/properties/new')({
  component: NewPropertyPage,
});

/**
 * Sign up a property.
 *
 * THE SAME SHAPE AS `/properties/PR000001`, deliberately. Identity card on top, then the two
 * columns: the ownership split where the tenancies index sits, and the fee and details down the
 * side. Every panel is in the place it will be after saving, so the form is a rehearsal of the
 * record rather than a different screen that happens to produce one. What is read-only there is
 * a field here; nothing else moves.
 *
 * Ownership cannot be skipped. A property with no owner cannot receive rent — there is nobody
 * for the money to belong to — and every trust invariant is about money belonging to a named
 * beneficiary. Refusing at sign-up costs one form field; discovering it when the first receipt
 * arrives costs a suspense entry and an explanation.
 *
 * The share editor takes PERCENTAGES because that is what a management agreement says, and
 * sends BASIS POINTS because that is what money divides by. The running total is shown while
 * typing for the same reason the journal form shows "out by 4.50": 33.33% three times is the
 * mistake this whole design exists to prevent, and it looks completely right.
 */
function NewPropertyPage() {
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();
  const today = todayIso();

  const owners = useQuery(trpc.property.owners.queryOptions(undefined, { staleTime: 30_000 }));
  const managers = useQuery(
    trpc.property.assignableManagers.queryOptions(undefined, { staleTime: 15_000 }),
  );
  const org = useOrgSettings();

  const [unit, setUnit] = useState('');
  const [streetNumber, setStreetNumber] = useState('');
  const [street, setStreet] = useState('');
  const [suburb, setSuburb] = useState('');
  const [city, setCity] = useState('');
  const [postcode, setPostcode] = useState('');
  const [type, setType] = useState<(typeof PROPERTY_TYPES)[number]>('house');
  const [bedrooms, setBedrooms] = useState('');
  const [bathrooms, setBathrooms] = useState('');
  const [managementStartsOn, setManagementStartsOn] = useState(today);
  const [feePercent, setFeePercent] = useState('');

  const [lines, setLines] = useState<ShareLine[]>(() => [emptyShare('s0')]);
  const [managerUserIds, setManagerUserIds] = useState<string[]>([]);
  const [nextKey, setNextKey] = useState(1);

  const create = useMutation(
    trpc.property.create.mutationOptions({
      meta: { errorTitle: 'Could not create that property' },
      onSuccess(result) {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.property.list.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.property.owners.queryKey() });
        void navigate({
          to: '/properties/$ref',
          params: { ref: formatRef('property', result.refSeq) },
        });
      },
    }),
  );

  const gst = org.data?.gstRegistered === true;
  const totals = shareTotals(lines);
  const ownershipIssues = shareIssues(lines);

  // Empty is "no fee agreed", which is a different answer from zero and is why it is not
  // defaulted. Anything else has to parse, and has to survive the same check the server makes.
  const feeBp = feePercent.trim() === '' ? null : parseSharePercent(feePercent);
  const feeIssues =
    feePercent.trim() === ''
      ? []
      : feeBp === null
        ? ['That is not a percentage']
        : managementFeeIssues({ feeBp, gst });

  // The same rules the edit form runs — see `propertyDetailIssues`. Shared rather than
  // restated, because the copy that drifts is always the validation.
  const addressIssues = propertyDetailIssues({ streetNumber, street, city });

  const managerIssues =
    managers.data !== undefined && managerUserIds.length === 0
      ? ['Assign at least one property manager']
      : [];
  const issues = [...addressIssues, ...ownershipIssues, ...feeIssues, ...managerIssues];

  const update = (key: string, patch: Partial<ShareLine>) =>
    setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const addLine = () => {
    setLines((current) => [...current, emptyShare(`s${nextKey}`)]);
    setNextKey((n) => n + 1);
  };

  const removeLine = (key: string) =>
    setLines((current) => (current.length === 1 ? current : current.filter((l) => l.key !== key)));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (issues.length > 0) return;

    const beds = wholeNumber(bedrooms);
    const baths = wholeNumber(bathrooms);

    create.mutate({
      ...(unit.trim() === '' ? {} : { unit: unit.trim() }),
      streetNumber: streetNumber.trim(),
      street: street.trim(),
      ...(suburb.trim() === '' ? {} : { suburb: suburb.trim() }),
      city: city.trim(),
      ...(postcode.trim() === '' ? {} : { postcode: postcode.trim() }),
      type,
      // The key is omitted rather than sent as undefined — `exactOptionalPropertyTypes` makes
      // those different types, and an absent room count means "not recorded", not zero.
      ...(beds === null ? {} : { bedrooms: beds }),
      ...(baths === null ? {} : { bathrooms: baths }),
      managementStartsOn,
      ...(feeBp === null ? {} : { managementFeeBp: feeBp }),
      shares: usableShares(lines),
      managerUserIds,
    });
  };

  const noOwners = owners.data !== undefined && owners.data.length === 0;

  // What the identity card will say once this is saved — filled in as it is typed, so the
  // heading is the record's own heading rather than a form title.
  const heading = streetLine({ unit: unit.trim() || null, streetNumber, street }).trim();

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="New property"
        subtitle="Address, management start, and who owns it"
        actions={
          <Link to="/properties">
            <Button size="sm" variant="secondary">
              All properties
            </Button>
          </Link>
        }
      />

      {noOwners ? (
        <div
          role="note"
          className="mb-4 rounded-md bg-warn-soft px-4 py-2.5 text-[0.8125rem] text-warn ring-1 ring-warn/25"
        >
          <strong className="font-bold">No owners yet.</strong> A property needs at least one, so
          create the owner first —{' '}
          <Link to="/owners" className="underline">
            Owners
          </Link>
          .
        </div>
      ) : null}

      <form onChange={unsaved.touch} onSubmit={submit} noValidate className="flex flex-col gap-4">
        {/* ── Identity ───────────────────────────────────────────────────────── */}
        {/*
         * The address IS the property's identity, so it sits where the heading sits on the
         * record — and the heading above the fields fills in as they are typed, which is the
         * cheapest possible check that "12A" went in the unit box and not the number box.
         */}
        <Panel className="p-5">
          <h2 className="truncate text-[1.375rem] font-extrabold leading-tight tracking-[-0.02em] text-ink">
            {heading === '' ? <span className="text-ink-subtle">New property</span> : heading}
          </h2>

          {/*
           * THE FASTEST PATH THROUGH THE FORM, and the first control on it. Sign-up is where a
           * property's address is typed from a management agreement by somebody who has never
           * seen the house — which is where the street names get misspelled, and a misspelled
           * street is a property that sits unmapped and `not_found` for ever after.
           *
           * It fills the five boxes below and nothing else. Ownership, the fee and the start date
           * are facts about the AGREEMENT and no address lookup has an opinion about them.
           */}
          <AddressSearch
            className="mt-3"
            onPick={(address) => {
              setUnit(address.unit ?? '');
              setStreetNumber(address.streetNumber);
              setStreet(address.street);
              // Cleared when LINZ has no suburb, rather than left: a pick REPLACES the address,
              // and a suburb held over from a previous search is how a property ends up filed in
              // a suburb it is not in.
              setSuburb(address.suburb ?? '');
              setCity(address.city);
            }}
          />

          <div className="mt-3 grid gap-3 sm:grid-cols-[6rem_8rem_minmax(0,1fr)]">
            <Field label="Unit" hint="Flat number">
              {(props) => (
                <Input {...props} value={unit} onChange={(e) => setUnit(e.target.value)} />
              )}
            </Field>
            <Field label="Number" required>
              {(props) => (
                <Input
                  {...props}
                  value={streetNumber}
                  onChange={(e) => setStreetNumber(e.target.value)}
                />
              )}
            </Field>
            <Field label="Street" required>
              {(props) => (
                <Input
                  {...props}
                  value={street}
                  onChange={(e) => setStreet(e.target.value)}
                  placeholder="Kowhai St"
                />
              )}
            </Field>
          </div>

          <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_8rem_11rem]">
            <Field label="Suburb">
              {(props) => (
                <Input {...props} value={suburb} onChange={(e) => setSuburb(e.target.value)} />
              )}
            </Field>
            <Field label="Town or city" required>
              {(props) => (
                <Input
                  {...props}
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Auckland"
                />
              )}
            </Field>
            <Field label="Postcode">
              {(props) => (
                <Input
                  {...props}
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  className="tabular-nums"
                />
              )}
            </Field>
            {/*
             * On the record this is the chip and "since" line in the top-right of the identity
             * card. It earns its place up here for the same reason: it decides commission
             * entitlement, so it is a fact about the management relationship, not a detail.
             */}
            <Field label="Management starts" required hint="Drives commission entitlement">
              {(props) => (
                <Input
                  {...props}
                  type="date"
                  value={managementStartsOn}
                  onChange={(e) => setManagementStartsOn(e.target.value)}
                />
              )}
            </Field>
          </div>
        </Panel>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
            {/* ── Ownership ────────────────────────────────────────────────── */}
            <Panel className="p-5">
              <PanelHeader
                title="Ownership"
                subtitle="Shares must total 100%"
                className="p-0 pb-3"
                aside={
                  <Button size="sm" variant="secondary" onClick={addLine} disabled={noOwners}>
                    <span aria-hidden="true">+</span> Add owner
                  </Button>
                }
              />

              <ul className="flex flex-col gap-2">
                {lines.map((line) => (
                  <li key={line.key} className="flex items-end gap-2">
                    <Field label="Owner" className="min-w-0 flex-1">
                      {(props) => (
                        <Select
                          {...props}
                          value={line.ownerId}
                          onChange={(e) => update(line.key, { ownerId: e.target.value })}
                        >
                          <option value="">Choose an owner…</option>
                          {(owners.data ?? []).map((owner) => (
                            <option key={owner.id} value={owner.id}>
                              {owner.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Field>

                    <Field label="Share" className="w-[7rem] shrink-0">
                      {(props) => (
                        <Input
                          {...props}
                          inputMode="decimal"
                          value={line.percent}
                          onChange={(e) => update(line.key, { percent: e.target.value })}
                          placeholder="100"
                          className="text-right tabular-nums"
                        />
                      )}
                    </Field>

                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeLine(line.key)}
                      aria-label="Remove this owner"
                      className="mb-1.5"
                      disabled={lines.length === 1}
                    >
                      ×
                    </Button>
                  </li>
                ))}
              </ul>

              <div className="mt-3 flex items-center gap-2 border-t border-rule pt-3 text-[0.8125rem]">
                <span className="text-ink-muted">Allocated</span>
                <span className="font-semibold tabular-nums">{formatShare(totals.totalBp)}</span>
                {/*
                 * Shown while typing, not on submit. 33.33% three times is the mistake basis
                 * points exist to prevent and it looks completely correct until something adds
                 * it up.
                 */}
                {totals.whole ? (
                  <Chip tone="positive">Whole</Chip>
                ) : (
                  <Chip tone="warn">
                    {totals.outByBp > 0
                      ? `${formatShare(totals.outByBp)} unallocated`
                      : `${formatShare(-totals.outByBp)} over`}
                  </Chip>
                )}
              </div>

              <p className="mt-3 text-[0.75rem] text-ink-subtle">
                Recorded from the day management starts. Changing the split afterwards is
                time-sliced and needs a trust role — it redirects money that is already flowing.
              </p>
            </Panel>

            <Panel className="p-5">
              <PanelHeader
                title="Property team"
                subtitle="At least one responsible staff member is required"
                className="p-0 pb-3"
              />
              {managers.isPending ? (
                <p className="text-[0.8125rem] text-ink-muted">Loading staff…</p>
              ) : managers.data?.length ? (
                <PropertyManagerPicker
                  managers={managers.data}
                  selected={managerUserIds}
                  onChange={setManagerUserIds}
                />
              ) : (
                <p className="text-[0.8125rem] text-warn">
                  No active property staff are available. Add a user with a property role first.
                </p>
              )}
            </Panel>
          </div>

          <div className="flex flex-col gap-4">
            {/* ── Management fee ───────────────────────────────────────────── */}
            {/*
             * Editable here for the reason it is worth having on this form at all: the
             * management agreement being typed in is the document the rate was negotiated on.
             * Same panel, same position and the same validation as `/properties/PR000001`.
             */}
            <Panel className="p-5">
              <PanelHeader
                title="Management fee"
                subtitle="A percentage of rent collected"
                className="p-0 pb-3"
              />

              <Field label="Rate" hint="Leave empty if none is agreed yet">
                {(props) => (
                  <Input
                    {...props}
                    inputMode="decimal"
                    placeholder="8"
                    value={feePercent}
                    onChange={(e) => setFeePercent(e.target.value)}
                    className="tabular-nums"
                  />
                )}
              </Field>

              {feeBp !== null && feeBp > 0 && feeIssues.length === 0 ? (
                <p className="mt-3 rounded-md bg-surface-sunk px-3 py-2 text-[0.8125rem]">
                  {formatFeeRate(feeBp)}
                  {gst ? ' + GST' : ''} — on $600 rent that is{' '}
                  <Amount
                    value={managementFee(60_000n, { feeBp, gst }).totalCents}
                    currency
                    className="font-semibold"
                  />{' '}
                  off the owner.
                </p>
              ) : null}

              {feeIssues.length > 0 ? (
                <p role="alert" className="mt-2 text-[0.75rem] text-warn">
                  {feeIssues[0]}
                </p>
              ) : null}

              <p className="mt-3 text-[0.75rem] text-ink-subtle">
                Charged on rent <strong className="font-semibold">collected</strong>, never on rent
                merely invoiced.{' '}
                {gst
                  ? 'GST is added because this agency is registered.'
                  : 'No GST — this agency is not registered.'}{' '}
                <Link to="/settings/tax" className="font-medium text-accent hover:underline">
                  Agency-wide setting
                </Link>
              </p>
            </Panel>

            {/* ── The property ─────────────────────────────────────────────── */}
            <Panel className="p-5">
              <PanelHeader title="Details" className="p-0 pb-3" />

              <div className="flex flex-col gap-3">
                <Field label="Type" required>
                  {(props) => (
                    <Select
                      {...props}
                      value={type}
                      onChange={(e) => setType(e.target.value as (typeof PROPERTY_TYPES)[number])}
                    >
                      {PROPERTY_TYPES.map((value) => (
                        <option key={value} value={value}>
                          {propertyTypeLabel(value)}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Bedrooms">
                    {(props) => (
                      <Input
                        {...props}
                        inputMode="numeric"
                        value={bedrooms}
                        onChange={(e) => setBedrooms(e.target.value)}
                        className="tabular-nums"
                      />
                    )}
                  </Field>

                  <Field label="Bathrooms">
                    {(props) => (
                      <Input
                        {...props}
                        inputMode="numeric"
                        value={bathrooms}
                        onChange={(e) => setBathrooms(e.target.value)}
                        className="tabular-nums"
                      />
                    )}
                  </Field>
                </div>

                {/*
                 * NO HEALTHY HOMES HERE ANY MORE, and the empty space is deliberate.
                 *
                 * §10 asks a create page to be the record's own skeleton, and says that where a
                 * panel genuinely cannot be filled in at creation the page SAYS SO rather than
                 * faking a field — which is what this dropdown was. Signing a property up happens
                 * off a management agreement, before anybody has been to the house; the honest
                 * answer is always "not assessed", and a required-looking select next to the
                 * bedroom count invites a guess about whether a house is lawful to let.
                 *
                 * So the record has a tab for it and this form has a sentence.
                 */}
                <p className="text-[0.8125rem] text-ink-muted">
                  Healthy Homes is recorded on the property’s own Healthy Homes tab once it exists —
                  the five standards, the assessor’s report and the compliance statement. There is
                  nothing to record here yet.
                </p>
              </div>
            </Panel>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            type="submit"
            size="lg"
            disabled={issues.length > 0 || managers.isPending || create.isPending}
          >
            {create.isPending ? 'Creating' : 'Create property'}
          </Button>
          <Link to="/properties">
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Link>
          {issues.length > 0 ? (
            <p className="text-[0.8125rem] text-ink-muted">{issues[0]}</p>
          ) : null}
        </div>
      </form>
    </div>
  );
}
